import React, { useEffect, useMemo, useState } from "react";
import ReactDOM from "react-dom";
import {
  adminPreviewPartnerInvoice,
  adminCreatePartnerInvoice,
  adminSendPartnerInvoice,
} from "../core/axios/endpoints";
import { useBodyScrollLock } from "../core/scroll-lock";
import { lineModuleLabel } from "../core/partner-subsidie";

const fmtEUR = (n) =>
  Number(n ?? 0).toLocaleString("nl-NL", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const today = () => new Date().toISOString().slice(0, 10);
const plus14 = () => {
  const d = new Date();
  d.setDate(d.getDate() + 14);
  return d.toISOString().slice(0, 10);
};

/**
 * PartnerInvoiceBuilderModal
 *
 * Geopend met een set aanvragen (BtwApplications of, via
 * `applicationModel="Applications"`, subsidie-aanvragen) die allemaal
 * dezelfde partnerUserId moeten hebben (validatie gebeurt in de parent voor
 * de open-knop). Roept
 * de preview-endpoint aan, laat regels editable bewerken, slaat de factuur
 * op (draft) en optioneel direct verzenden.
 */
const PartnerInvoiceBuilderModal = ({
  isOpen,
  onClose,
  onCreated,
  partnerUserId,
  applicationIds,
  defaultBillingEmail,
  applicationModel = "BtwApplications",
}) => {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(null);
  const [lines, setLines] = useState([]);
  const [billingEmail, setBillingEmail] = useState(defaultBillingEmail ?? "");
  const [kenmerk, setKenmerk] = useState("");
  const [invoiceDate, setInvoiceDate] = useState(today());
  const [dueDate, setDueDate] = useState(plus14());
  const [notes, setNotes] = useState("");
  const [sendAfterCreate, setSendAfterCreate] = useState(true);

  useBodyScrollLock(isOpen);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    adminPreviewPartnerInvoice({ partnerUserId, applicationIds, applicationModel })
      .then((res) => {
        if (cancelled) return;
        const data = res?.data ?? res;
        setPreview(data);
        // Elke regel draagt zijn eigen model mee, zodat create dezelfde
        // collectie raakt als de preview (contract 7).
        setLines(
          (data?.lines ?? []).map((l) => ({
            ...l,
            applicationModel: l.applicationModel ?? applicationModel,
          })),
        );
        if (!billingEmail) setBillingEmail(data?.partner?.billingEmail ?? "");
      })
      .catch((e) => {
        if (cancelled) return;
        setError(
          e?.response?.data?.error ??
            e?.message ??
            "Preview kon niet worden geladen.",
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, partnerUserId, applicationIds, applicationModel]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape" && !submitting) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, submitting, onClose]);

  const totals = useMemo(() => {
    const subtotal = lines.reduce(
      (acc, l) => acc + Number(l.amountExcl ?? 0),
      0,
    );
    const vat = lines.reduce((acc, l) => acc + Number(l.vatAmount ?? 0), 0);
    const total = lines.reduce((acc, l) => acc + Number(l.amountIncl ?? 0), 0);
    return {
      subtotalExcl: Math.round(subtotal * 100) / 100,
      vatAmount: Math.round(vat * 100) / 100,
      totalIncl: Math.round(total * 100) / 100,
    };
  }, [lines]);

  const updateLineAmount = (idx, raw) => {
    const excl = Number(raw);
    if (!Number.isFinite(excl)) return;
    setLines((prev) => {
      const next = [...prev];
      const vatRate = next[idx].vatRate ?? 0.21;
      const vatAmount = Math.round(excl * vatRate * 100) / 100;
      next[idx] = {
        ...next[idx],
        amountExcl: excl,
        vatAmount,
        amountIncl: Math.round((excl + vatAmount) * 100) / 100,
      };
      return next;
    });
  };

  const removeLine = (idx) => {
    setLines((prev) => prev.filter((_, i) => i !== idx));
  };

  const updateLineDescription = (idx, value) => {
    setLines((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], description: value };
      return next;
    });
  };

  const submit = async () => {
    if (lines.length === 0) return;
    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        partnerUserId,
        applicationModel,
        lines,
        kenmerk: kenmerk || undefined,
        invoiceDate: invoiceDate || undefined,
        dueDate: dueDate || undefined,
        billingEmail: billingEmail || undefined,
        notes: notes || undefined,
      };
      const created = await adminCreatePartnerInvoice(payload);
      const invoice = created?.data ?? created;
      if (sendAfterCreate && invoice?._id) {
        await adminSendPartnerInvoice(invoice._id).catch((e) => {
          console.error("Versturen direct na aanmaken mislukt:", e);
          throw new Error(
            "Factuur is aangemaakt als draft, maar versturen mislukte. Probeer opnieuw vanaf het facturen-overzicht.",
          );
        });
      }
      onCreated?.(invoice, { sent: sendAfterCreate });
    } catch (e) {
      setError(
        e?.response?.data?.error ??
          e?.message ??
          "Factuur aanmaken is mislukt.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;
  if (typeof document === "undefined") return null;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[10000] flex items-start justify-center overflow-y-auto bg-[#091E428A] p-4">
      <div className="my-8 w-full max-w-3xl rounded-md bg-white shadow-overlay">
        <div className="flex items-start justify-between gap-4 px-6 pt-6">
          <div>
            <h2 className="text-xl font-medium text-ink">
              Partner-factuur aanmaken
            </h2>
            <p className="mt-1 text-sm text-ink-subtlest">
              {preview?.partner?.naam
                ? `Voor ${preview.partner.naam}`
                : "Selecteer aanvragen om te factureren."}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded text-ink-subtlest hover:bg-[#091E420F] hover:text-ink disabled:opacity-50"
            aria-label="Sluiten"
          >
            <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M4.3 4.3a1 1 0 011.4 0L10 8.6l4.3-4.3a1 1 0 111.4 1.4L11.4 10l4.3 4.3a1 1 0 11-1.4 1.4L10 11.4l-4.3 4.3a1 1 0 11-1.4-1.4L8.6 10 4.3 5.7a1 1 0 010-1.4z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>

        <div className="max-h-[70vh] overflow-y-auto px-6 py-4 text-sm text-ink">
          {loading ? (
            <div className="py-12 text-center text-sm text-ink-subtlest">
              Preview laden…
            </div>
          ) : error ? (
            <div className="rounded bg-[#FFECEB] px-3 py-2 text-sm text-[#AE2A19]">
              {error}
            </div>
          ) : (
            <>
              {/* Header velden */}
              <div className="grid gap-4 sm:grid-cols-2">
                <label className="block text-sm">
                  <span className="text-xs font-semibold text-ink-subtlest">Kenmerk</span>
                  <input
                    type="text"
                    value={kenmerk}
                    onChange={(e) => setKenmerk(e.target.value)}
                    placeholder="(automatisch = factuurnummer)"
                    className="mt-1 w-full rounded border-2 border-line bg-white px-2 py-1.5 text-sm text-ink placeholder:text-ink-subtlest focus:border-brand focus:outline-none disabled:opacity-50"
                    disabled={submitting}
                  />
                </label>
                <label className="block text-sm">
                  <span className="text-xs font-semibold text-ink-subtlest">
                    Facturatie-email
                  </span>
                  <input
                    type="email"
                    value={billingEmail}
                    onChange={(e) => setBillingEmail(e.target.value)}
                    className="mt-1 w-full rounded border-2 border-line bg-white px-2 py-1.5 text-sm text-ink placeholder:text-ink-subtlest focus:border-brand focus:outline-none disabled:opacity-50"
                    disabled={submitting}
                    required
                  />
                </label>
                <label className="block text-sm">
                  <span className="text-xs font-semibold text-ink-subtlest">Factuurdatum</span>
                  <input
                    type="date"
                    value={invoiceDate}
                    onChange={(e) => setInvoiceDate(e.target.value)}
                    className="mt-1 w-full rounded border-2 border-line bg-white px-2 py-1.5 text-sm text-ink placeholder:text-ink-subtlest focus:border-brand focus:outline-none disabled:opacity-50"
                    disabled={submitting}
                  />
                </label>
                <label className="block text-sm">
                  <span className="text-xs font-semibold text-ink-subtlest">Vervaldatum</span>
                  <input
                    type="date"
                    value={dueDate}
                    onChange={(e) => setDueDate(e.target.value)}
                    className="mt-1 w-full rounded border-2 border-line bg-white px-2 py-1.5 text-sm text-ink placeholder:text-ink-subtlest focus:border-brand focus:outline-none disabled:opacity-50"
                    disabled={submitting}
                  />
                </label>
              </div>

              {/* Regels */}
              <div className="mt-6">
                <h3 className="text-[11px] font-semibold uppercase tracking-wide text-ink-subtlest">
                  Regels ({lines.length})
                </h3>
                {lines.length === 0 ? (
                  <p className="mt-3 rounded bg-sunken p-4 text-center text-sm text-ink-subtlest">
                    Geen regels. Annuleer en selecteer aanvragen die nog niet
                    op een verstuurde factuur staan.
                  </p>
                ) : (
                  <div className="mt-3 divide-y divide-line rounded border border-line">
                    {lines.map((line, idx) => (
                      <div
                        key={String(line.btwApplicationId) + idx}
                        className="flex flex-wrap items-start gap-3 p-3"
                      >
                        <div className="flex-1 min-w-[200px]">
                          <p className="font-medium text-ink">
                            {line.customerName || "-"}
                            <span className="ml-2 rounded bg-[#091E420F] px-1.5 py-px text-[11px] font-bold uppercase tracking-wide text-ink-subtle">
                              {lineModuleLabel(line)}
                            </span>
                          </p>
                          <input
                            type="text"
                            value={line.description}
                            onChange={(e) =>
                              updateLineDescription(idx, e.target.value)
                            }
                            className="mt-1 w-full rounded border-2 border-line bg-white px-2 py-1 text-xs text-ink focus:border-brand focus:outline-none disabled:opacity-50"
                            disabled={submitting}
                          />
                          {line.alreadyInvoiced ? (
                            <p className="mt-1 text-xs text-[#7F5F01]">
                              Let op: stond al op een eerdere factuur.
                            </p>
                          ) : null}
                        </div>
                        <div className="w-32">
                          <label className="block text-xs font-semibold text-ink-subtlest">
                            Bedrag excl.
                          </label>
                          <div className="mt-1 flex items-center gap-1">
                            <span className="text-sm text-ink-subtlest">&euro;</span>
                            <input
                              type="number"
                              step="0.01"
                              value={line.amountExcl}
                              onChange={(e) =>
                                updateLineAmount(idx, e.target.value)
                              }
                              className="w-full rounded border-2 border-line bg-white px-2 py-1 text-right text-sm tabular-nums text-ink focus:border-brand focus:outline-none disabled:opacity-50"
                              disabled={submitting}
                            />
                          </div>
                          <p className="mt-1 text-right text-xs tabular-nums text-ink-subtlest">
                            incl. &euro; {fmtEUR(line.amountIncl)}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeLine(idx)}
                          disabled={submitting}
                          className="mt-4 flex h-8 w-8 items-center justify-center rounded text-ink-subtlest hover:bg-[#FFECEB] hover:text-[#AE2A19] disabled:opacity-50"
                          aria-label="Regel verwijderen"
                        >
                          <svg
                            className="h-4 w-4"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zm-3 6a1 1 0 112 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Totals */}
              <div className="mt-4 flex justify-end">
                <div className="w-72 rounded bg-sunken p-3 text-sm tabular-nums text-ink">
                  <div className="flex justify-between py-0.5">
                    <span className="text-ink-subtlest">Subtotaal (excl.)</span>
                    <span>&euro; {fmtEUR(totals.subtotalExcl)}</span>
                  </div>
                  <div className="flex justify-between py-0.5">
                    <span className="text-ink-subtlest">BTW (21%)</span>
                    <span>&euro; {fmtEUR(totals.vatAmount)}</span>
                  </div>
                  <div className="mt-1 flex justify-between border-t border-line pt-1 font-semibold text-ink">
                    <span>Totaal (incl.)</span>
                    <span>&euro; {fmtEUR(totals.totalIncl)}</span>
                  </div>
                </div>
              </div>

              {/* Notes + send-flag */}
              <label className="mt-4 block text-sm">
                <span className="text-xs font-semibold text-ink-subtlest">
                  Opmerking (optioneel)
                </span>
                <textarea
                  rows={3}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="mt-1 w-full rounded border-2 border-line bg-white px-2 py-1.5 text-sm text-ink placeholder:text-ink-subtlest focus:border-brand focus:outline-none disabled:opacity-50"
                  disabled={submitting}
                  placeholder="Wordt onder op de factuur en PDF getoond."
                />
              </label>

              <label className="mt-4 flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={sendAfterCreate}
                  onChange={(e) => setSendAfterCreate(e.target.checked)}
                  disabled={submitting}
                  className="h-4 w-4 rounded border-line text-brand focus:ring-0"
                />
                <span>Direct versturen naar partner-email na aanmaken</span>
              </label>
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-6 pb-6 pt-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="h-8 rounded bg-[#091E420F] px-3 text-sm font-medium text-ink hover:bg-[#091E4224] disabled:cursor-not-allowed disabled:opacity-50"
          >
            Annuleren
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={submitting || loading || lines.length === 0 || !!error}
            className="h-8 rounded bg-brand px-3 text-sm font-medium text-white hover:bg-brand-hover disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting
              ? "Bezig…"
              : sendAfterCreate
              ? "Aanmaken & versturen"
              : "Aanmaken als draft"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default PartnerInvoiceBuilderModal;
